/**
 * Router compatibility shim — mirrors the Next.js `next/navigation` surface we
 * were using on top of TanStack Router. Lets migrated call sites keep calling
 * `router.push(url)`, `router.replace(url)`, `router.back()`, `usePathname()`,
 * `useSearchParams().get('id')` with zero behavior change.
 *
 * New code should prefer the native TanStack Router hooks
 * (`useRouter` / `useLocation` / `useSearch`) directly.
 */
import {
  useRouter as useTanStackRouter,
  useLocation,
  useSearch,
} from "@tanstack/react-router";
import { useMemo } from "react";

/**
 * Split a Next.js-style href into a TanStack Router target. Query keys are
 * decoded by URLSearchParams; when a key repeats, the last value wins.
 */
export function parseHref(href: string): {
  pathname: string;
  search: Record<string, string>;
} {
  const queryIndex = href.indexOf("?");
  if (queryIndex === -1) return { pathname: href, search: {} };

  const pathname = href.slice(0, queryIndex);
  const search: Record<string, string> = {};
  new URLSearchParams(href.slice(queryIndex + 1)).forEach((value, key) => {
    search[key] = value;
  });

  return { pathname, search };
}

export function useRouter() {
  const router = useTanStackRouter();
  // Memoize so the returned object is referentially stable across renders.
  // Without this, every call site that puts `router` in a useCallback/useEffect
  // dep array (e.g. AuthContext) recreates callbacks on every render, causing
  // infinite loops when those callbacks trigger state updates.
  return useMemo(() => ({
    push: (href: string) => {
      const { pathname, search } = parseHref(href);
      void router.navigate({ to: pathname as never, search: search as never });
    },
    replace: (href: string) => {
      const { pathname, search } = parseHref(href);
      void router.navigate({ to: pathname as never, search: search as never, replace: true });
    },
    back: () => router.history.back(),
    forward: () => router.history.forward(),
    refresh: () => {
      void router.invalidate();
    },
    prefetch: (href: string) => {
      const { pathname, search } = parseHref(href);
      void router.preloadRoute({ to: pathname as never, search: search as never });
    },
  }), [router]);
}

export type AppRouter = ReturnType<typeof useRouter>;

export function usePathname(): string {
  return useLocation({ select: (s) => s.pathname });
}

interface ReadonlyURLSearchParams {
  get(key: string): string | null;
  has(key: string): boolean;
  toString(): string;
  entries(): IterableIterator<[string, string]>;
  forEach(cb: (value: string, key: string) => void): void;
  [Symbol.iterator](): IterableIterator<[string, string]>;
}

export function useSearchParams(): ReadonlyURLSearchParams {
  const search = useSearch({ strict: false }) as Record<string, unknown>;

  const pairs: [string, string][] = Object.entries(search)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => [k, String(v)]);

  const params = new URLSearchParams(pairs);
  return {
    get: (key) => params.get(key),
    has: (key) => params.has(key),
    toString: () => params.toString(),
    entries: () => params.entries(),
    forEach: (cb) => params.forEach((v, k) => cb(v, k)),
    [Symbol.iterator]: () => params[Symbol.iterator](),
  };
}
